import { useState, type FormEvent } from "react";
import { siteInfo } from "@/mocks/site";

type Status = "idle" | "submitting" | "success" | "error";

const FORM_ENDPOINT = "https://readdy.ai/api/form/dahbtm3oh653ivfvnqu0";

const clueTypes = [
  "疑似谣言线索",
  "事件进展补充",
  "内容错误指正",
  "转载与授权咨询",
  "其他问题",
];

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [feedback, setFeedback] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);

    const honeypot = String(data.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setFeedback("已收到你的提交，我们会尽快核对。");
      form.reset();
      return;
    }

    data.delete("website_alt");

    const params = new URLSearchParams();
    data.forEach((value, key) => {
      if (typeof value === "string") {
        params.append(key, value);
      }
    });

    setStatus("submitting");
    setFeedback("");

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.meta?.detail ||
        parsed?.message ||
        "";

      const isSpam = /spam|form data is spam/i.test(`${serverMsg} ${responseText}`);
      const isOk = response.ok && parsed?.code === "OK" && !isSpam;

      if (isOk) {
        setStatus("success");
        setFeedback("已收到你的提交，我们会尽快核对并在有进展时回复你。");
        form.reset();
        return;
      }

      setStatus("error");
      setFeedback(
        serverMsg && !isSpam
          ? `提交未成功：${serverMsg}`
          : "提交未成功，请检查填写内容后重试，或直接发送邮件与我们联系。",
      );
    } catch {
      setStatus("error");
      setFeedback("网络异常，提交未成功。请稍后重试，或直接发送邮件与我们联系。");
    }
  };

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
      <h2 className="font-heading text-xl font-bold text-foreground-950">提交线索 / 联系我们</h2>
      <p className="mt-2 text-sm leading-relaxed text-foreground-600">
        附上原始链接或截图来源会大大提高核验效率。我们不会公开你的个人信息。
      </p>

      <form
        id="clue-contact-form"
        data-readdy-form
        onSubmit={handleSubmit}
        className="mt-6 space-y-5"
      >
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label htmlFor="contact-name" className="mb-1.5 block text-sm font-medium text-foreground-800">
              称呼
            </label>
            <input
              id="contact-name"
              name="name"
              type="text"
              required
              placeholder="如何称呼你"
              className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
            />
          </div>
          <div>
            <label htmlFor="contact-email" className="mb-1.5 block text-sm font-medium text-foreground-800">
              邮箱
            </label>
            <input
              id="contact-email"
              name="email"
              type="email"
              required
              placeholder="用于回复你的邮箱"
              autoComplete="email"
              className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
            />
          </div>
        </div>

        <div>
          <label htmlFor="contact-type" className="mb-1.5 block text-sm font-medium text-foreground-800">
            线索类型
          </label>
          <div className="relative">
            <select
              id="contact-type"
              name="type"
              required
              defaultValue={clueTypes[0]}
              className="w-full cursor-pointer appearance-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 pr-9 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
            >
              {clueTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
            <span className="pointer-events-none absolute right-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400" aria-hidden="true">
              <i className="ri-arrow-down-s-line text-base" />
            </span>
          </div>
        </div>

        <div>
          <label htmlFor="contact-message" className="mb-1.5 block text-sm font-medium text-foreground-800">
            线索或问题说明
          </label>
          <textarea
            id="contact-message"
            name="message"
            required
            rows={5}
            maxLength={500}
            placeholder="请描述看到的信息内容、原始来源与你希望核验的点（最多 500 字）"
            className="w-full resize-y rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
          <p className="mt-1.5 text-xs text-foreground-400">最多 500 字，请勿在其中填写个人敏感信息。</p>
        </div>

        <div className="alt-contact-note" aria-hidden="true">
          <label htmlFor="contact-website-alt">请勿填写此项</label>
          <input
            id="contact-website-alt"
            name="website_alt"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            readOnly
          />
        </div>

        <div className="flex flex-col gap-3 border-t border-background-200 pt-5 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs leading-relaxed text-foreground-500">
            也可直接发送邮件至{" "}
            <a href={`mailto:${siteInfo.email}`} className="font-medium text-primary-600 hover:text-primary-700">
              {siteInfo.email}
            </a>
          </p>
          <button
            type="submit"
            disabled={status === "submitting"}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
          >
            <i className="ri-send-plane-line text-base" aria-hidden="true" />
            <span className="whitespace-nowrap">{status === "submitting" ? "提交中…" : "提交线索"}</span>
          </button>
        </div>

        {status === "success" ? (
          <div
            role="status"
            className="flex items-start gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-4 py-3 text-sm text-secondary-900"
          >
            <span className="mt-0.5 flex h-4 w-4 items-center justify-center" aria-hidden="true">
              <i className="ri-checkbox-circle-line text-base" />
            </span>
            <span>{feedback}</span>
          </div>
        ) : null}

        {status === "error" ? (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-800"
          >
            <span className="mt-0.5 flex h-4 w-4 items-center justify-center" aria-hidden="true">
              <i className="ri-error-warning-line text-base" />
            </span>
            <span>{feedback}</span>
          </div>
        ) : null}
      </form>
    </div>
  );
}